import React, {useEffect, useLayoutEffect} from 'react';
import 'react-native-gesture-handler';
import {BackHandler, Text, Platform, SafeAreaView, Linking, View} from 'react-native';

// Serialize BigInt as string so JSON.stringify handles protobuf int64 fields.
// @bufbuild/protobuf v2 decodes int64 into bigint; matches the proto JSON wire encoding.
if (typeof BigInt !== 'undefined') {
  const bigIntPrototype = BigInt.prototype as unknown as { toJSON?: () => string };
  if (typeof bigIntPrototype.toJSON !== 'function') {
    bigIntPrototype.toJSON = function () {
      return this.toString();
    };
  }
}

// No-op BackHandler on web so libraries that register hardwareBackPress
// (e.g. @react-navigation/native's useBackButton) do not emit console warnings.
if (Platform.OS === 'web') {
  (BackHandler as unknown as {
    addEventListener: (event: string, handler: () => boolean) => { remove: () => void };
  }).addEventListener = () => ({remove: () => undefined});
}
import * as serviceWorkerRegistration from "./src/serviceWorkerRegistration";
import {Main} from './src/components/Main';
import {StoreContextProvider} from './src/components/StoreContextProvider';
import {ConnectClientProvider} from './src/grpc/ConnectClientContext';
import MainStyle from "./MainStyle";
import {useFonts} from 'expo-font';
import {amplitudeApiKey, FixForTournamentFlatListAlignment, isFullSwing} from "./src/constants";
import {initAnalytics} from './src/services/analytics';
import {buildSafeCardTokenizationErrorLog, prepareCardTokenization} from './src/services/cardTokenization';
import * as faceapi from "face-api.js";
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import {colors} from "./src/assets/styles/colors";
import {Provider as PaperProvider, MD3DarkTheme as DefaultTheme} from 'react-native-paper';
import useIOS16NoBounce from "./src/hooks/useIOS16NoBounce";

const theme = {
  ...DefaultTheme, colors: {...DefaultTheme.colors, onSurface: 'white', surfaceVariant: '#252526', error: '#C73232'}
};

// @ts-ignore
Text.defaultProps = Text.defaultProps || {};
// @ts-ignore
Text.defaultProps.allowFontScaling = false;

const App = () => {
  useFonts({
    'Barlow-Regular': require('./src/assets/fonts/Barlow-Regular.ttf'),
    'Barlow-Medium': require('./src/assets/fonts/Barlow-Medium.ttf'),
    'Barlow-SemiBold': require('./src/assets/fonts/Barlow-SemiBold.ttf'),
    'Barlow-Bold': require('./src/assets/fonts/Barlow-Bold.ttf'),
    // 'Alternox-Regular': require('./src/assets/fonts/AlternoxRegular.ttf'),
    // 'Alternox-SemiBold': require('./src/assets/fonts/AlternoxSemiBold.ttf'),
    // 'Alternox-Bold': require('./src/assets/fonts/AlternoxBold.ttf'),
    'DavisSans-Regular': require('./src/assets/fonts/Davis_Sans_Regular.otf'),
    'DavisSans-Medium': require('./src/assets/fonts/Davis_Sans_Medium.otf'),
    'POI Aeronaut': require('./src/assets/fonts/POI_Aeronaut.otf'),
    'POI Orbiter': require('./src/assets/fonts/POI_Orbiter_Bold.otf'),
    'StratosBoldItalic': require('./src/assets/fonts/Stratos-bolditalic.otf'),
  });
  useIOS16NoBounce();

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    void prepareCardTokenization().catch(error => {
      console.warn('[CardTokenization] SDK preload failed', buildSafeCardTokenizationErrorLog(error));
    });
  }, []);

  useEffect(() => {
    (async () => {
      await faceapi.nets.tinyFaceDetector.loadFromUri('/models');
      await faceapi.nets.faceLandmark68Net.loadFromUri('/models');

      try {
        await initAnalytics(amplitudeApiKey);
      } catch (e) {
        console.error('[Analytics] Failed to initialize Amplitude:', e);
      }

      if (Platform.OS === 'web') {
        const url = await Linking.getInitialURL();

        if (url && url.startsWith('http://') && !url.includes('localhost')) {
          const httpsURL = url.replace('http://', 'https://');
          window.open(httpsURL, '_self');
        }

        const ensureMeta = (name: string, content: string) => {
          let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
          if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('name', name);
            document.head.appendChild(meta);
          }
          meta.setAttribute('content', content);
        };

        ensureMeta('theme-color', isFullSwing ? '#252526' : colors.new_bg);

        // adding ApplePay SDK
        const scriptTag = document.createElement("script");
        scriptTag.src = 'https://applepay.cdn-apple.com/jsapi/v1.1.0/apple-pay-sdk.js';
        document.getElementsByTagName("head")[0].appendChild(scriptTag);
      }
    })();
  }, []);

  useLayoutEffect(() => {
    if (Platform.OS === 'web') document.body.insertAdjacentHTML('beforeend', FixForTournamentFlatListAlignment);
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <PaperProvider theme={theme}>
        <View
          style={[MainStyle.flex1, MainStyle.fix_gradient_and_modal_error_in_landscape, isFullSwing ? {backgroundColor: 'rgb(19,19,20)'} : MainStyle.bg_new_bg]}>
          <SafeAreaView style={MainStyle.flex1}>
            <ConnectClientProvider>
              <StoreContextProvider>
                <Main/>
              </StoreContextProvider>
            </ConnectClientProvider>
          </SafeAreaView>
        </View>
      </PaperProvider>
    </LocalizationProvider>
  );
};

if (Platform.OS === 'web') serviceWorkerRegistration.register();

export default App;
